import React, { ElementType, FunctionComponent, ReactNode } from "react";
import { Transition } from "react-transition-group";
import { styled } from "@mui/material/styles";
import Box, { BoxProps } from "@mui/material/Box";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import IconButton from "../button/IconButton";
import FlexColumn, { FlexColumnProps } from "./FlexColumn";
import { ICON_MEDIUM_SIZE, STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";

const duration = 300;
const halfIconHeight = ICON_MEDIUM_SIZE / 2;

const PREFIX = "StyledFlexColumn";

const classes = {
	button: `${PREFIX}button`,
	top: `${PREFIX}top`,
	middle: `${PREFIX}middle`,
};

const StyledFlexColumn = styled(FlexColumn)(({
	theme,
}) => ({
	[`&:hover .${classes.button}`]: {
		display: "inline-flex",
	},
	[`& .${classes.button}`]: {
		// background is necessary for button so that pane border does not show through button
		backgroundColor: theme.palette.background.paper,
		display: "none",
		left: `calc(50% - ${halfIconHeight}px)`,
		position: "absolute",
		top: -halfIconHeight,
		zIndex: 1,
	},
	[`& .${classes.top}`]: {
		transition: `height ${duration}ms`,
	},
	[`& .${classes.middle}`]: {
		// currently bug does not allow use of border on one side and borderColor together on Box:
		// https://github.com/mui-org/material-ui/issues/16995
		borderBottom: STANDARD_BORDER_STYLE,
		position: "relative",
	},
}));

interface FlexTopDrawerProps extends Omit<FlexColumnProps, "children"> {
	/** child components of drawer (when open and closed) and bottom of container */
	children: {
		bottom: ReactNode;
		top: ReactNode;
		topWhenClosed?: ReactNode;
	};

	/** hides the open/close button */
	disabled?: boolean;

	/* height of drawer when closed */
	heightClosed?: number;

	/** height of drawer when open; if excluded, then height is "auto" */
	heightOpen?: number;

	/** hides splitter and button to open/close */
	hideSplitter?: boolean;

	/** indicates if drawer is expanded */
	open: boolean;

	/** callback when open state of drawer is changed */
	onToggleOpen?: (open: boolean) => void;

	/** callback when drawer finishes its transition */
	onTransition?: () => void;

	/** render top portion when closed even if topWhenClosed child is not specified */
	renderWhenClosed?: boolean;
};

const FlexTopDrawer: FunctionComponent<FlexTopDrawerProps> = (props) => {
	const {
		children,
		disabled,
		heightClosed,
		heightOpen,
		hideSplitter,
		open,
		onToggleOpen,
		onTransition,
		renderWhenClosed,
		...rootProps
	} = props;

	const {
		top,
		topWhenClosed,
		bottom,
	} = children;

	const middleProps: BoxProps = hideSplitter ? { display: "none" } : null;
	const minHeight = heightClosed ?? ICON_MEDIUM_SIZE;
	const transitionStyles = {
		exited: { height: minHeight },
		exiting: { height: minHeight },
	};

	const onClick = () => {
		onToggleOpen(!open);

		if (onTransition) {
			setTimeout(onTransition, duration);
		}
	};

	return (
		<StyledFlexColumn
			{...rootProps}
		>
			<Transition in={open} timeout={duration}>
				{(state) => (
					<Box
						className={classes.top}
						flexShrink={0}
						height={heightOpen || "auto"}
						overflow="hidden"
						style={transitionStyles[state]}
					>
						{state !== "exited" ? top : (topWhenClosed || (renderWhenClosed ? top : null))}
					</Box>
				)}
			</Transition>
			<Box className={classes.middle} {...middleProps}>
				{!disabled && (
					<IconButton
						aria-label={open ? "collapse" : "expand"}
						className={classes.button}
						design="outline"
						onClick={onClick}
						round
					>
						{open ?
							<ExpandLess />
							:
							<ExpandMore />
						}
					</IconButton>
				)}
			</Box>
			<Box flexGrow={1} overflow="auto">
				{bottom}
			</Box>
		</StyledFlexColumn>
	);
};

export default FlexTopDrawer;
